import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concat, EMPTY, from, Observable, of } from "rxjs";
import {
  catchError,
  concatMap,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
} from "rxjs/operators";
import { AdminService } from "src/app/shared/services/admin/admin.service";
import {
  createTeamTeachersLoad,
  createTeamTeachersFail,
  createTeamTeachersSuccess,
} from "../../actions/admin/create-team-teachers.actions";
import {
  courseTeachersDelete,
  courseTeachersListLoad,
  courseTeachersListReset,
  courseTeachersListUpdate,
} from "../../actions/admin/course-teachers.actions";
import {
  editTeamTeachers,
  editTeamTeachersFail,
  editTeamTeachersSuccess,
} from "../../actions/admin/edit-team-teachers.actions";
import {
  deleteTeamTeacherLoad,
  deleteTeamTeacherLoadFail,
} from "../../actions/admin/delete-team-teacher.action";
import { NotificationsService } from "src/app/shared/services/notifications/notifications.service";
import { createNotification } from "src/app/shared/utils/create-notification";
import { TranslateService } from "@ngx-translate/core";
import {
  deleteActiveTeachersSubgroup,
  updateActiveTeachersSubgroup,
} from "../../actions/admin/course-admin.actions";
import {
  packageDeleteSubgroup,
  packageListLoad,
  packageListReset,
  packageUpdateSubgroup,
} from "../../actions/packages/packages.actions";
import { SubGroup } from "src/app/shared/interfaces/orgadmin/course-organization";
import { WarningModalService } from "src/app/shared/services/warning-modal/warning-modal.service";
import { EditTeamTeachersContext } from "src/app/shared/enums/edit-limits-context";

@Injectable()
export class CreateTeamTeachersEffect {
  createTeamTeachersList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createTeamTeachersLoad),
      mergeMap((param) =>
        this.adminService
          .createTeamTeachers(param.organizationId, param.subId, param.payload)
          .pipe(
            switchMap((teachers: SubGroup) => {
              this.notificationsService.showToastr(
                createNotification({
                  content: {
                    message: this.translate.instant(param.notificationMessage, {
                      name: param.name,
                    }),
                  },
                }),
              );

              const actions = [createTeamTeachersSuccess()];

              if (param.subId && !param.packageId) {
                actions.push(courseTeachersListUpdate({ teachers }));
              } else if (param.packageId) {
                actions.push(
                  packageUpdateSubgroup({
                    packageId: param.packageId,
                    subgroup: teachers,
                    courseId: param.courseId,
                  }),
                );
              }

              return from(actions);
            }),
            catchError(({ error }) => of(createTeamTeachersFail({ error }))),
          ),
      ),
    ),
  );

  editTeamTeachersList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(editTeamTeachers),
      mergeMap((param) => {
        return this.adminService
          .editTeamTeachers(
            param.OrganizationId,
            param.SubscriptionId,
            param.TeamId,
            param.payload,
          )
          .pipe(
            switchMap((teachers: SubGroup) => {
              const message = this.translate.instant(
                param.notificationMessage,
                {
                  name: param.payload.name,
                },
              );

              this.notificationsService.showToastr(
                createNotification({
                  content: { message },
                }),
              );

              switch (param.context) {
                case EditTeamTeachersContext.FromCoursePage:
                  return [
                    editTeamTeachersSuccess(),
                    courseTeachersListUpdate({
                      teachers,
                    }),
                    courseTeachersListLoad({
                      organizationId: param.OrganizationId,
                      subId: param.SubscriptionId,
                    }),
                  ];

                case EditTeamTeachersContext.FromGlobalCoursesPage:
                  return [
                    editTeamTeachersSuccess(),
                    updateActiveTeachersSubgroup({
                      teachers,
                      courseAdminId: param.SubscriptionId,
                    }),
                    ...(param.packageId
                      ? [
                          packageUpdateSubgroup({
                            subgroup: teachers,
                            packageId: param.packageId,
                            courseId: param.courseId,
                          }),
                        ]
                      : []),
                  ];

                case EditTeamTeachersContext.FromPackagePage:
                  return [
                    editTeamTeachersSuccess(),
                    packageUpdateSubgroup({
                      subgroup: teachers,
                      packageId: param.packageId,
                      courseId: param.courseId,
                    }),
                  ];

                default:
                  return of(editTeamTeachersSuccess());
              }
            }),
            catchError(({ error }) => {
              this.warningModalService.showModal({
                modalTitle: "GLOBAL.MESSAGES.INFO",
                text: error?.limit[0],
                icon: "info-outline",
              });
              return of(editTeamTeachersFail({ error }));
            }),
          );
      }),
    ),
  );

  deleteTeamTeachersList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteTeamTeacherLoad),
      concatMap((param) => {
        const optimisticDelete$ = of(
          courseTeachersDelete({ teamId: param.teamId }),
        );

        const packageDelete$ = param.packageId
          ? of(
              packageDeleteSubgroup({
                packageId: param.packageId,
                subgroupId: param.teamId,
              }),
            )
          : EMPTY;

        const serviceRequest$ = this.adminService
          .deleteTeamTeachers(param.organizationId, param.subId, param.teamId)
          .pipe(
            tap(() => {
              this.notificationsService.showToastr(
                createNotification({
                  content: {
                    message: this.translate.instant(param.notificationMessage, {
                      name: param.teamName,
                    }),
                  },
                }),
              );
            }),
            map(() =>
              deleteActiveTeachersSubgroup({
                courseAdminId: param.subId,
                subgroupId: param.teamId,
              }),
            ),
            catchError((error) => this.handleDeleteError(error, param)),
          );

        return concat(optimisticDelete$, packageDelete$, serviceRequest$);
      }),
    ),
  );

  private handleDeleteError(error: any, param: any) {
    const actions = [
      deleteTeamTeacherLoadFail({ error }),
      courseTeachersListReset(),
      courseTeachersListLoad({
        organizationId: param.organizationId,
        subId: param.subId,
      }),
    ];

    if (param.packageId) {
      actions.push(
        packageListReset(),
        packageListLoad({ id: param.organizationId }),
      );
    }

    return from(actions);
  }

  constructor(
    private adminService: AdminService,
    private actions$: Actions,
    private notificationsService: NotificationsService,
    private translate: TranslateService,
    private warningModalService: WarningModalService,
  ) {}
}
